/**
  * SEVS Expiry Management page for admin users.
  * Allows admins to view and manage SEVS expiry records, update renewal status, and review archived SEVS.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import { useAuth, supabase } from '../../context/AuthContext';
import { convertToMelbourneDateAndTime } from '../../utils/dateUtils';

const SEVS_STATUSES = ['Expired', 'To Be Expired', 'Applied Renew', 'Approved Sevs Renewal', 'MR Variation Applied'];
const TOAST_DURATION_MS = 7000;

const ChevronIcon = ({ open }) => (
  open ? (
    // Up arrow
    <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 15l-7-7-7 7" />
    </svg>
  ) : (
    // Down arrow
    <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
);

const CrossIcon = () => (
  <svg className="h-4 w-4 text-rose-600 transition-colors duration-200 group-hover:text-rose-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const modalShellClassName = 'w-full max-w-[29rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]';
const modalHeaderClassName = 'px-6 pt-6';
const modalBodyClassName = 'px-6 pb-5 pt-3';
const modalFooterClassName = 'flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-4 sm:flex-row sm:justify-end';
const modalPrimaryButtonClassName = 'inline-flex min-w-[92px] items-center justify-center rounded-[0.65rem] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const modalSecondaryButtonClassName = 'inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
const modalBackdropClassName = 'fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]';

const ModalHeader = ({ eyebrow, title, description, tone = 'slate' }) => {
  const toneClasses = {
    slate: 'bg-slate-900 text-white',
    blue: 'bg-sky-100 text-sky-700',
    violet: 'bg-violet-100 text-violet-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-orange-100 text-orange-700',
    rose: 'bg-red-100 text-red-700',
  };

  return (
    <div className={modalHeaderClassName}>
      <div className={`inline-flex items-center rounded-[0.55rem] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${toneClasses[tone] || toneClasses.slate}`}>
        {eyebrow}
      </div>
      <div className="mt-2.5">
        <h3 className="text-[0.95rem] font-semibold leading-6 text-slate-900">{title}</h3>
        {description ? <p className="mt-2 max-w-[30rem] text-sm leading-6 text-slate-500">{description}</p> : null}
      </div>
    </div>
  );
};

const getSevsStatusBadgeClassName = (status) => {
  switch (status) {
    case 'Expired':
      return 'bg-red-100 text-red-700';
    case 'Applied Renew':
      return 'bg-violet-100 text-violet-700';
    case 'Approved Sevs Renewal':
      return 'bg-sky-100 text-sky-700';
    case 'MR Variation Applied':
      return 'bg-emerald-100 text-emerald-700';
    case 'To Be Expired':
    default:
      return 'bg-orange-100 text-orange-700';
  }
};

const SevsExpiryManagement = () => {
  const navigate = useNavigate();
  const [sevsData, setSevsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openActivityId, setOpenActivityId] = useState(null);
  const [modalId, setModalId] = useState(null);
  const [appliedRenewModalId, setAppliedRenewModalId] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState('Total');
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const [sevsApprovalModalId, setSevsApprovalModalId] = useState(null);
  const [sevsApprovalConfirmed, setSevsApprovalConfirmed] = useState(false);
  const [mrVariationApprovedModalId, setMrVariationApprovedModalId] = useState(null);
  const [mrVariationApprovalConfirmed, setMrVariationApprovalConfirmed] = useState(false);
  const [cantBeRenewedModalId, setCantBeRenewedModalId] = useState(null);
  const [cantBeRenewedConfirmed, setCantBeRenewedConfirmed] = useState(false);
  const [cantBeRenewedReason, setCantBeRenewedReason] = useState('');
  const [cantBeRenewedReasonError, setCantBeRenewedReasonError] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  const showToast = (title, message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ title, message });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, TOAST_DURATION_MS);
  };

  // Parses DD/MM/YYYY SEVS expiry text into a timestamp for sorting.
  // Bad or missing dates sort to the bottom.
  const parseSevsExpiryDate = (value) => {
    if (!value) return Number.NEGATIVE_INFINITY;

    const parts = value.split('/').map(Number);
    if (parts.length === 3 && parts.every(part => !Number.isNaN(part))) {
      const [day, month, year] = parts;
      return new Date(year, month - 1, day).getTime();
    }

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  };

  const sortSevsDataByExpiry = (rows, order = sortOrder) => {
    return [...rows].sort((a, b) => {
      const aDate = parseSevsExpiryDate(a.sevs_expiry);
      const bDate = parseSevsExpiryDate(b.sevs_expiry);
      return order === 'desc' ? bDate - aDate : aDate - bDate;
    });
  };

  // Reloads the SEVS list joined with the linked model report details.
  const refreshSevsData = async () => {
    const { data, error } = await supabase
      .from('sevs_expiry_management')
      .select(`
        *,
        customer_model_reports!inner(
          id,
          make,
          model,
          chassis_code,
          sevs_entry,
          sevs_expiry,
          mr_approval
        )
      `)
      .in('status', SEVS_STATUSES)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const transformedData = data.map(item => ({
      id: item.id,
      mr_id: item.mr_id,
      status: item.status,
      sevs_activity_logs: item.sevs_activity_logs,
      created_at: item.created_at,
      updated_at: item.updated_at,
      sevs_expiry: item.sevs_expiry || item.customer_model_reports.sevs_expiry,
      sevs_approval_no: item.sevs_approval_no,
      mr_wi_reference: item.mr_wi_reference,
      mr_vc_reference: item.mr_vc_reference,
      make: item.customer_model_reports.make,
      model: item.customer_model_reports.model,
      chassis_code: item.customer_model_reports.chassis_code,
      sevs_entry: item.customer_model_reports.sevs_entry,
      mr_approval: item.customer_model_reports.mr_approval
    }));

    setSevsData(sortSevsDataByExpiry(transformedData));
  };

  useEffect(() => {
    const fetchSevsData = async () => {
      setLoading(true);
      setError(null);
      try {
        await refreshSevsData();
      } catch (err) {
        setError(err.message);
        setSevsData([]);
      }
      setLoading(false);
    };
    fetchSevsData();

    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const counts = useMemo(() => {
    return {
      'Total': sevsData.length,
      'Expired': sevsData.filter(d => d.status === 'Expired').length,
      'To Be Expired': sevsData.filter(d => d.status === 'To Be Expired').length,
      'Applied Renew': sevsData.filter(d => d.status === 'Applied Renew').length,
      'Approved Sevs Renewal': sevsData.filter(d => d.status === 'Approved Sevs Renewal').length,
      'MR Variation Applied': sevsData.filter(d => d.status === 'MR Variation Applied').length,
    };
  }, [sevsData]);

  const filteredData = useMemo(() => {
    if (activeFilter === 'Total') {
      return sevsData;
    }
    return sevsData.filter(d => d.status === activeFilter);
  }, [sevsData, activeFilter]);

  const displayedData = useMemo(() => {
    return sortSevsDataByExpiry(filteredData);
  }, [filteredData, sortOrder]);

  const findRowById = (id) => (id == null ? null : displayedData.find((row) => row.id === id) ?? sevsData.find((row) => row.id === id) ?? null);

  const selectedExpiryRow = findRowById(modalId);
  const selectedExpiryStatus = selectedExpiryRow?.status;
  const selectedExpiryTone = selectedExpiryStatus === 'Expired' ? 'rose' : 'amber';
  const selectedExpiryEyebrow = selectedExpiryStatus === 'Expired' ? 'Expired' : 'To be Expired';

  const toggleActivityPanel = (rowId) => {
    setOpenActivityId((current) => (current === rowId ? null : rowId));
  };

  const changeActiveFilter = (nextFilter) => {
    setActiveFilter(nextFilter);
    setOpenActivityId(null);
  };

  // Helper to parse activity log
  const parseActivityLog = (logObj) => {
    if (!logObj || typeof logObj !== 'object') return [];
    return Object.entries(logObj)
      .flatMap(([timestamp, users]) =>
        Object.values(users).map(user => ({
          date: timestamp,
          user: user.name,
          email: user.email,
          action: user.status,
          reason: user.reason
        }))
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // newest first
  };

  return (
    <AdminLayout
      activeItemId="sevs-expiry"
      title="SEVS Expiry Management"
      subtitle="Track SEVS expiry, update renewal status, and review archived SEVS."
      contentClassName="w-full max-w-none"
      footerContainerClassName="mt-10 lg:mt-14"
    >
      {toast && (
        <div className="pointer-events-none fixed bottom-6 right-6 z-[70] flex justify-end px-0">
          <div className="pointer-events-auto w-full max-w-sm rounded-[1rem] border border-emerald-200 bg-white px-4 py-3 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.35)] ring-1 ring-emerald-100/80">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.2 7.26a1 1 0 0 1-1.42.008L3.3 9.18a1 1 0 1 1 1.4-1.428l4.08 4.002 6.5-6.456a1 1 0 0 1 1.424-.008Z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{toast.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{toast.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-md font-medium uppercase tracking-[0.04em] ">SEVS Status Overview</p>
              <p className="mt-1 text-sm text-slate-500">Select status to filter the records below.</p>
            </div>
          </div>

          <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <button
              onClick={() => changeActiveFilter('Total')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Total' ? 'border-blue-700 bg-blue-600 text-white shadow-lg shadow-blue-200/60 ring-1 ring-blue-200/70 scale-[1.01]' : 'border-blue-200 bg-white hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Total' ? 'text-white' : 'text-slate-500'}`}>Total</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Total' ? 'text-white' : 'text-slate-900'}`}>{counts['Total']}</p>
            </button>
            <button
              onClick={() => changeActiveFilter('To Be Expired')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'To Be Expired' ? 'border-orange-300 bg-orange-100 text-orange-900 shadow-lg shadow-orange-100/80 ring-1 ring-orange-200/80 scale-[1.01]' : 'border-orange-200 bg-white hover:border-orange-300 hover:bg-orange-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'To Be Expired' ? 'text-orange-900' : 'text-slate-500'}`}>To Be Expired</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'To Be Expired' ? 'text-orange-900' : 'text-orange-600'}`}>{counts['To Be Expired']}</p>
            </button>
            <button
              onClick={() => changeActiveFilter('Expired')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Expired' ? 'border-red-300 bg-red-100 text-red-900 shadow-lg shadow-red-100/80 ring-1 ring-red-200/80 scale-[1.01]' : 'border-red-200 bg-white hover:border-red-300 hover:bg-red-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Expired' ? 'text-red-900' : 'text-slate-500'}`}>Expired</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Expired' ? 'text-red-900' : 'text-red-600'}`}>{counts['Expired']}</p>
            </button>
            <button
              onClick={() => changeActiveFilter('Applied Renew')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Applied Renew' ? 'border-violet-300 bg-violet-100 text-violet-900 shadow-lg shadow-violet-100/80 ring-1 ring-violet-200/80 scale-[1.01]' : 'border-violet-200 bg-white hover:border-violet-300 hover:bg-violet-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Applied Renew' ? 'text-violet-900' : 'text-slate-500'}`}>Applied Renew</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Applied Renew' ? 'text-violet-900' : 'text-violet-600'}`}>{counts['Applied Renew']}</p>
            </button>
            <button
              onClick={() => changeActiveFilter('Approved Sevs Renewal')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Approved Sevs Renewal' ? 'border-sky-300 bg-sky-100 text-sky-900 shadow-lg shadow-sky-100/80 ring-1 ring-sky-200/80 scale-[1.01]' : 'border-sky-200 bg-white hover:border-sky-300 hover:bg-sky-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Approved Sevs Renewal' ? 'text-sky-900' : 'text-slate-500'}`}>Approved Sevs Renewal</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Approved Sevs Renewal' ? 'text-sky-900' : 'text-sky-600'}`}>{counts['Approved Sevs Renewal']}</p>
            </button>
            <button
              onClick={() => changeActiveFilter('MR Variation Applied')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'MR Variation Applied' ? 'border-emerald-300 bg-emerald-100 text-emerald-900 shadow-lg shadow-emerald-100/80 ring-1 ring-emerald-200/80 scale-[1.01]' : 'border-emerald-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'MR Variation Applied' ? 'text-emerald-900' : 'text-slate-500'}`}>MR Variation Applied</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'MR Variation Applied' ? 'text-emerald-900' : 'text-emerald-600'}`}>{counts['MR Variation Applied']}</p>
            </button>
          </div>

          <div className="mt-4 sm:hidden">
            <select
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              value={activeFilter}
              onChange={(event) => changeActiveFilter(event.target.value)}
            >
              <option value="Total">All Statuses</option>
              <option value="To Be Expired">To Be Expired</option>
              <option value="Expired">Expired</option>
              <option value="Applied Renew">Applied Renew</option>
              <option value="Approved Sevs Renewal">Approved Sevs Renewal</option>
              <option value="MR Variation Applied">MR Variation Applied</option>
            </select>

            <div className="relative mt-4 inline-flex w-full items-center">
              <select
                aria-label="SEVS expiry order"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                className="w-full appearance-none rounded-2xl border border-slate-300 bg-white bg-none px-4 py-3 pr-10 text-sm text-slate-900 shadow-sm transition [-moz-appearance:none] [-webkit-appearance:none] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="desc">SEVS Expiry (desc)</option>
                <option value="asc">SEVS Expiry (asc)</option>
              </select>
              <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">SEVS Expiring in 30 Days</h3>
            </div>
            <div className="hidden items-center gap-3 self-start sm:flex sm:self-auto">
              <div className="relative inline-flex items-center">
                <select
                  aria-label="SEVS expiry order"
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value)}
                  className="min-w-[190px] appearance-none rounded-xl border border-slate-200 bg-white bg-none py-2 pl-3 pr-9 text-sm font-medium text-slate-700 shadow-sm outline-none transition-colors duration-200 [-moz-appearance:none] [-webkit-appearance:none] hover:border-slate-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="desc">SEVS Expiry (desc)</option>
                  <option value="asc">SEVS Expiry (asc)</option>
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
            <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-slate-50/80">
                <tr>
                  <th></th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:table-cell xl:px-4 xl:py-4">S. No</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:px-4 xl:py-4">Car Details</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:px-4 xl:py-4">Chassis Code</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell xl:px-4 xl:py-4">SEVS Entry</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell xl:px-4 xl:py-4">SEVS Expiry</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell xl:px-4 xl:py-4">MR Approval</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:px-4 xl:py-4">Status</th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:px-4 xl:py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-red-500">{error}</td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500">No SEVS records found for this filter.</td>
                  </tr>
                ) : (
                  displayedData.map((row, idx) => (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td
                          className="px-2 py-3 text-center align-middle cursor-pointer xl:py-4"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          <button
                            aria-label={openActivityId === row.id ? 'Hide Activity' : 'Show Activity'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleActivityPanel(row.id);
                            }}
                            className="focus:outline-none"
                          >
                            <ChevronIcon open={openActivityId === row.id} />
                          </button>
                        </td>
                        <td
                          className="hidden px-2 py-3 text-sm text-slate-900 cursor-pointer sm:table-cell xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {idx + 1}
                        </td>
                        <td
                          className="px-2 py-3 break-words text-sm text-slate-900 cursor-pointer xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {row.make} {row.model}
                        </td>
                        <td
                          className="px-2 py-3 break-words text-sm text-slate-900 cursor-pointer xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {row.chassis_code}
                        </td>
                        <td
                          className="hidden px-2 py-3 break-words text-sm text-slate-900 cursor-pointer xl:table-cell xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {row.sevs_entry}
                        </td>
                        <td
                          className="hidden px-2 py-3 break-words text-sm text-slate-900 cursor-pointer xl:table-cell xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {row.sevs_expiry}
                        </td>
                        <td
                          className="hidden px-2 py-3 break-words text-sm text-slate-900 cursor-pointer xl:table-cell xl:px-4 xl:py-4 xl:text-base"
                          onClick={() => toggleActivityPanel(row.id)}
                        >
                          {row.mr_approval}
                        </td>
                        <td className="px-2 py-3 text-sm text-slate-900 xl:px-4 xl:py-4 xl:text-base">
                          <button
                            className="mr-1.5 inline-flex items-center justify-center rounded-[0.8rem] border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 xl:mr-2 xl:px-3.5 xl:py-2"
                            onClick={() => navigate(`/admin/model-reports/${row.mr_id}`, { state: { from: '/sevs-expiry' } })}
                          >
                            View
                          </button>
                          <span
                            className={`inline-flex max-w-full break-words px-2.5 py-1 text-xs font-semibold ${getSevsStatusBadgeClassName(row.status)} ${row.status === 'To Be Expired' || row.status === 'Applied Renew' || row.status === 'Approved Sevs Renewal' || row.status === 'MR Variation Applied' ? 'cursor-pointer' : 'cursor-default'} rounded-full xl:px-3`}
                            onClick={row.status === 'To Be Expired' || row.status === 'Expired' ? () => setModalId(row.id) : row.status === 'Applied Renew' ? () => {
                              setSevsApprovalModalId(row.id);
                              setSevsApprovalConfirmed(false);
                            } : row.status === 'Approved Sevs Renewal' ? () => {
                              setAppliedRenewModalId(row.id);
                              setIsConfirmed(false);
                            } : row.status === 'MR Variation Applied' ? () => {
                              setMrVariationApprovedModalId(row.id);
                              setMrVariationApprovalConfirmed(false);
                            } : undefined}
                          >
                            {row.status === 'Applied Renew'
                              ? 'Applied Renew'
                              : row.status === 'To Be Expired'
                                ? 'To be Expired'
                                : row.status === 'Approved Sevs Renewal'
                                  ? 'Approved Sevs Renewal'
                                : row.status === 'MR Variation Applied'
                                  ? 'MR Variation Applied'
                                  : 'Expired'}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-center xl:px-4 xl:py-4 xl:pr-4">
                          <div className="relative inline-block group">
                            <button
                              type="button"
                              aria-label="Can't be renewed"
                              onClick={() => {
                                setCantBeRenewedModalId(row.id);
                                setCantBeRenewedConfirmed(false);
                              }}
                              className="inline-flex items-center justify-center"
                            >
                              <CrossIcon />
                            </button>
                            <div className="pointer-events-none absolute bottom-full right-0 mb-2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 z-10">
                              Can't be renewed
                            </div>
                          </div>
                        </td>
                      </tr>
                      {openActivityId === row.id && (
                        <tr>
                          <td colSpan={10} className="bg-slate-50 px-4 py-4">
                            <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
                              <div className="grid gap-4">
                                <div className="border-b border-slate-200 xl:hidden">
                                  <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm sm:h-11 sm:w-11">
                                        <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
                                        </svg>
                                      </span>
                                      <div>
                                        <p className="text-sm font-medium uppercase tracking-[0.04em] xl:text-md">Details</p>
                                        <h4 className="text-xs font-medium text-slate-500 xl:text-sm">Review the SEVS record details.</h4>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="px-5 py-4">
                                    <div className="grid gap-3 sm:grid-cols-3">
                                      <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">SEVS Entry</p>
                                        <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.sevs_entry || '-'}</p>
                                      </div>
                                      <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">SEVS Expiry</p>
                                        <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.sevs_expiry || '-'}</p>
                                      </div>
                                      <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">MRE Approval</p>
                                        <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.mr_approval || '-'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm sm:h-11 sm:w-11">
                                      <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <circle cx="4" cy="12" r="1.5" />
                                        <circle cx="4" cy="6" r="1.5" />
                                        <circle cx="4" cy="18" r="1.5" />
                                        <line x1="8" y1="6" x2="20" y2="6" strokeLinecap="round" />
                                        <line x1="8" y1="12" x2="20" y2="12" strokeLinecap="round" />
                                        <line x1="8" y1="18" x2="20" y2="18" strokeLinecap="round" />
                                      </svg>
                                    </span>
                                    <div>
                                      <p className="text-md font-medium uppercase tracking-[0.04em] xl:text-md">Activity Logs</p>
                                      <h4 className="text-sm font-medium text-slate-500 xl:text-sm">View email notification history.</h4>
                                    </div>
                                  </div>
                                  <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 ring-1 ring-slate-200 uppercase tracking-[0.04em] xl:text-xs">
                                    {parseActivityLog(row.sevs_activity_logs).length} entries
                                  </span>
                                </div>

                                {parseActivityLog(row.sevs_activity_logs).length === 0 ? (
                                  <div className="px-5 py-8 text-sm text-slate-400 xl:text-sm">No activity found.</div>
                                ) : (
                                  <div>
                                    <div className="hidden border-b border-slate-200 bg-slate-50 px-5 py-3 lg:grid lg:grid-cols-[minmax(220px,1.1fr)_minmax(220px,1fr)_minmax(260px,1.2fr)] lg:gap-4">
                                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 xl:text-xs">Date</p>
                                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 xl:text-xs">Email</p>
                                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 xl:text-xs">Action</p>
                                    </div>
                                    <div className="divide-y divide-slate-200">
                                    {parseActivityLog(row.sevs_activity_logs).map((log, i) => (
                                      <div key={i} className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(220px,1.1fr)_minmax(220px,1fr)_minmax(260px,1.2fr)]">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 lg:hidden">Date</p>
                                            <p className="mt-2 text-xs font-normal text-slate-600 lg:mt-0 xl:text-sm">{convertToMelbourneDateAndTime(log.date)}</p>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 lg:hidden">Email</p>
                                            <p className="mt-2 break-all text-xs font-normal text-slate-600 lg:mt-0 xl:text-sm">{log.email}</p>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 lg:hidden">Action</p>
                                          <div className="mt-2 flex flex-wrap items-start gap-2 lg:mt-0">
                                            <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200 xl:text-xs">
                                              {log.action}
                                            </span>
                                          </div>
                                          {log.reason && (
                                            <div className="mt-3 rounded-2xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-normal text-orange-800 xl:text-sm">
                                              <span className="font-semibold">Reason:</span> {log.reason}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
      {modalId !== null && createPortal(
        <div className={modalBackdropClassName}>
          <div className={modalShellClassName}>
            <ModalHeader
              eyebrow={selectedExpiryEyebrow}
              title="Confirm SEVS renewal applied"
              tone={selectedExpiryTone}
            />
            <div className={modalBodyClassName}>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                Has the SEVS renewal been successfully applied for this model report?
              </div>
            </div>
            <div className={modalFooterClassName}>
              <button
                className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
                onClick={async () => {
                  const row = findRowById(modalId);
                  if (row && row.id) {
                    // 1. Get the current activity log
                    let activityLog = row.sevs_activity_logs && typeof row.sevs_activity_logs === 'object' && !Array.isArray(row.sevs_activity_logs)
                      ? { ...row.sevs_activity_logs }
                      : {};

                    // 2. Add new entry with logged-in user info
                    const currentTimestamp = new Date().toISOString();
                    const userEmail = user?.email || "Unknown";
                    activityLog[currentTimestamp] = {
                      [userEmail]: {
                        email: userEmail,
                        status: "SEVS Renewal Applied"
                      }
                    };

                    // 3. Update DB
                    const { error: updateError } = await supabase
                      .from('sevs_expiry_management')
                      .update({ status: 'Applied Renew', sevs_activity_logs: activityLog })
                      .eq('id', row.id);

                    if (updateError) {
                      alert('Error updating status: ' + updateError.message);
                      console.log(updateError);
                    } else {
                      await refreshSevsData();
                      showToast(
                        'SEVS renewal is applied',
                        'You can continue tracking the next renewal steps from this page.'
                      );
                    }
                  }
                  setOpenActivityId(null);
                  setModalId(null);
                }}
              >
                Applied
              </button>
              <button
                className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
                onClick={() => setModalId(null)}
              >
                Not yet
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {sevsApprovalModalId !== null && createPortal(
        <div className={modalBackdropClassName}>
          <div className={modalShellClassName}>
            <ModalHeader
              eyebrow="Applied Renew"
              title="SEVS approval confirmation"
              tone="violet"
            />
            <div className={modalBodyClassName}>
              <p className="text-sm leading-6 text-slate-600">Review the confirmation below and continue only when the department approval is complete.</p>
              {/* Confirmation Checkbox */}
              <div className="mt-5 flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="flex items-center h-5">
                  <input
                    id="sevsApprovalConfirmation"
                    name="sevsApprovalConfirmation"
                    type="checkbox"
                    checked={sevsApprovalConfirmed}
                    onChange={(e) => setSevsApprovalConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="sevsApprovalConfirmation" className="text-slate-700">
                    I confirm that the SEVS has been approved by the Department
                  </label>
                </div>
              </div>
            </div>
            <div className={modalFooterClassName}>
              <button
                className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
                disabled={!sevsApprovalConfirmed}
                onClick={async () => {
                  const row = findRowById(sevsApprovalModalId);
                  if (row && row.id) {
                    // 1. Get the current activity log
                    let activityLog = row.sevs_activity_logs && typeof row.sevs_activity_logs === 'object' && !Array.isArray(row.sevs_activity_logs)
                      ? { ...row.sevs_activity_logs }
                      : {};

                    // 2. Add new entry with logged-in user info
                    const currentTimestamp = new Date().toISOString();
                    const userEmail = user?.email || "Unknown";
                    activityLog[currentTimestamp] = {
                      [userEmail]: {
                        email: userEmail,
                        status: "SEVS Renewal Approved by Department."
                      }
                    };

                    // 3. Update DB
                    const { error: updateError } = await supabase
                      .from('sevs_expiry_management')
                      .update({ status: 'Approved Sevs Renewal', sevs_activity_logs: activityLog })
                      .eq('id', row.id);

                    if (updateError) {
                      alert('Error updating status: ' + updateError.message);
                      console.log(updateError);
                    } else {
                      await refreshSevsData();
                      showToast(
                        'SEVS renewal is approved',
                        'You can continue to confirm MR variation when ready.'
                      );
                    }
                  }
                  setOpenActivityId(null);
                  setSevsApprovalModalId(null);
                  setSevsApprovalConfirmed(false);
                }}
              >
                Confirm
              </button>
              <button
                className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
                onClick={() => {
                  setSevsApprovalModalId(null);
                  setSevsApprovalConfirmed(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {appliedRenewModalId !== null && createPortal(
        <div className={modalBackdropClassName}>
          <div className={modalShellClassName}>
            <ModalHeader
              eyebrow="Approved SEVS Renewal"
              title="MR variation confirmation"
              tone="blue"
            />
            <div className={modalBodyClassName}>
              <p className="text-sm leading-6 text-slate-600">Review the confirmation below and continue only when the MR variation has been applied successfully.</p>
              {/* Confirmation Checkbox */}
              <div className="mt-5 flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="flex items-center h-5">
                  <input
                    id="mrVariationConfirmation"
                    name="mrVariationConfirmation"
                    type="checkbox"
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="mrVariationConfirmation" className="text-slate-700">
                    I confirm that the MR Variation has been applied
                  </label>
                </div>
              </div>
            </div>
            <div className={modalFooterClassName}>
              <button
                className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
                disabled={!isConfirmed}
                onClick={async () => {
                  const row = findRowById(appliedRenewModalId);
                  if (row && row.id) {
                    // 1. Get the current activity log
                    let activityLog = row.sevs_activity_logs && typeof row.sevs_activity_logs === 'object' && !Array.isArray(row.sevs_activity_logs)
                      ? { ...row.sevs_activity_logs }
                      : {};

                    // 2. Add new entry with logged-in user info
                    const currentTimestamp = new Date().toISOString();
                    const userEmail = user?.email || "Unknown";
                    activityLog[currentTimestamp] = {
                      [userEmail]: {
                        email: userEmail,
                        status: "MR Variation Applied"
                      }
                    };

                    // 3. Update DB
                    const { error: updateError } = await supabase
                      .from('sevs_expiry_management')
                      .update({ status: 'MR Variation Applied', sevs_activity_logs: activityLog })
                      .eq('id', row.id);

                    if (updateError) {
                      alert('Error updating status: ' + updateError.message);
                      console.log(updateError);
                    } else {
                      await refreshSevsData();
                      showToast(
                        'MR variation is applied',
                        'You can safely cross or close this record when ready.'
                      );
                    }
                  }
                  setOpenActivityId(null);
                  setAppliedRenewModalId(null);
                  setIsConfirmed(false);
                }}
              >
                Confirm
              </button>
              <button
                className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
                onClick={() => {
                  setAppliedRenewModalId(null);
                  setIsConfirmed(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {mrVariationApprovedModalId !== null && createPortal(
        <div className={modalBackdropClassName}>
          <div className={modalShellClassName}>
            <ModalHeader
              eyebrow="MR Variation Applied"
              title="MR variation approval confirmation"
              tone="emerald"
            />
            <div className={modalBodyClassName}>
              <p className="text-sm leading-6 text-slate-600">Review the confirmation below and continue only when the MR variation has been approved by the Department.</p>
              <div className="mt-5 flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="flex items-center h-5">
                  <input
                    id="mrVariationApprovalConfirmation"
                    name="mrVariationApprovalConfirmation"
                    type="checkbox"
                    checked={mrVariationApprovalConfirmed}
                    onChange={(e) => setMrVariationApprovalConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-300"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="mrVariationApprovalConfirmation" className="text-slate-700">
                    I confirm that the MR Variation has been approved by the Department
                  </label>
                </div>
              </div>
            </div>
            <div className={modalFooterClassName}>
              <button
                className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
                disabled={!mrVariationApprovalConfirmed}
                onClick={async () => {
                  const row = findRowById(mrVariationApprovedModalId);
                  if (row && row.id) {
                    let activityLog = row.sevs_activity_logs && typeof row.sevs_activity_logs === 'object' && !Array.isArray(row.sevs_activity_logs)
                      ? { ...row.sevs_activity_logs }
                      : {};
                    const currentTimestamp = new Date().toISOString();
                    const userEmail = user?.email || "Unknown";
                    activityLog[currentTimestamp] = {
                      [userEmail]: {
                        email: userEmail,
                        status: "Done"
                      }
                    };
                    const { error: updateError } = await supabase
                      .from('sevs_expiry_management')
                      .update({
                        status: 'Done',
                        sevs_activity_logs: activityLog
                      })
                      .eq('id', row.id);

                    if (updateError) {
                      alert('Error updating status: ' + updateError.message);
                      console.log(updateError);
                    } else {
                      await refreshSevsData();
                      showToast(
                        'MR variation is approved',
                        'The record is complete. You can safely close it.'
                      );
                    }
                  }
                  setOpenActivityId(null);
                  setMrVariationApprovedModalId(null);
                  setMrVariationApprovalConfirmed(false);
                }}
              >
                Confirm
              </button>
              <button
                className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
                onClick={() => {
                  setMrVariationApprovedModalId(null);
                  setMrVariationApprovalConfirmed(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {cantBeRenewedModalId !== null && createPortal(
        <div className={modalBackdropClassName}>
          <div className={modalShellClassName}>
            <ModalHeader
              eyebrow="Can't be renewed"
              title="Archive SEVS record"
              tone="slate"
            />
            <div className={modalBodyClassName}>
              <p className="text-sm leading-6 text-slate-600">Archive this record only when the SEVS cannot be renewed and should no longer remain active.</p>
              {/* Reason Field */}
              <div className="mt-5 mb-4">
                <label className="mb-1 block text-sm font-medium text-slate-700">Reason <span className="text-rose-500">*</span></label>
                <textarea
                  className="w-full rounded-[0.8rem] border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
                  value={cantBeRenewedReason}
                  onChange={e => {
                    setCantBeRenewedReason(e.target.value);
                    setCantBeRenewedReasonError('');
                  }}
                  rows={2}
                  placeholder="Enter the reason why SEVS cannot be renewed"
                />
                {cantBeRenewedReasonError && <div className="mt-1 text-xs text-rose-600">{cantBeRenewedReasonError}</div>}
              </div>
              {/* Confirmation Checkbox */}
              <div className="flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="flex items-center h-5">
                  <input
                    id="cantBeRenewedConfirmation"
                    name="cantBeRenewedConfirmation"
                    type="checkbox"
                    checked={cantBeRenewedConfirmed}
                    onChange={(e) => setCantBeRenewedConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-700"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="cantBeRenewedConfirmation" className="text-slate-700">
                    I confirm that this SEVS cannot be renewed
                  </label>
                </div>
              </div>
            </div>
            <div className={modalFooterClassName}>
              <button
                className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
                disabled={!cantBeRenewedConfirmed || !cantBeRenewedReason.trim()}
                onClick={async () => {
                  if (!cantBeRenewedReason.trim()) {
                    setCantBeRenewedReasonError('Reason is required');
                    return;
                  }
                  const row = findRowById(cantBeRenewedModalId);
                  if (row && row.id) {
                    // 1. Get the current activity log
                    let activityLog = row.sevs_activity_logs && typeof row.sevs_activity_logs === 'object' && !Array.isArray(row.sevs_activity_logs)
                      ? { ...row.sevs_activity_logs }
                      : {};

                    // 2. Add new entry with logged-in user info
                    const currentTimestamp = new Date().toISOString();
                    const userEmail = user?.email || "Unknown";
                    activityLog[currentTimestamp] = {
                      [userEmail]: {
                        email: userEmail,
                        status: "SEVS cannot be renewed - moved to archived",
                        reason: cantBeRenewedReason
                      }
                    };

                    // 3. Update DB to archived status
                    const { error: updateError } = await supabase
                      .from('sevs_expiry_management')
                      .update({ status: 'Cant be renewed', sevs_activity_logs: activityLog })
                      .eq('id', row.id);

                    if (updateError) {
                      alert('Error updating status: ' + updateError.message);
                      console.log(updateError);
                    } else {
                      await refreshSevsData();
                      showToast(
                        'SEVS cannot be renewed',
                        'The record has been moved to archived. You can safely close it.'
                      );
                    }
                  }
                  setOpenActivityId(null);
                  setCantBeRenewedModalId(null);
                  setCantBeRenewedConfirmed(false);
                  setCantBeRenewedReason('');
                  setCantBeRenewedReasonError('');
                }}
              >
                Confirm
              </button>
              <button
                className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
                onClick={() => {
                  setCantBeRenewedModalId(null);
                  setCantBeRenewedConfirmed(false);
                  setCantBeRenewedReason('');
                  setCantBeRenewedReasonError('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
};

export default SevsExpiryManagement;
